/**
 * @file pages/PricingPage.js
 * @description Public pricing page. Three tiers (Solo / Crew / Pro) with
 * annual/monthly toggle, founding-member pricing, and FAQ.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Button } from '../design-system';
import PublicTopNav from '../components/PublicTopNav';
import PublicFooter from '../components/PublicFooter';

const TIERS = [
  {
    code: 'solo',
    name: 'Solo',
    monthlyPrice: 79,
    annualPrice: 790,
    foundingPrice: 49,
    foundingSpots: 100,
    badge: null,
    tagline: 'For solo inspectors flying solo.',
    inspections: 'Unlimited',
    users: '1 user',
    ai: 'Sonnet 4.6 review on every submit',
    features: [
      'Customizable inspection templates',
      'Mobile-first inspection forms',
      'Photo capture + auto-organized',
      'AI review (Sonnet 4.6) — flag issues before submit',
      'PDF branded reports',
      'Basic CRM (clients, properties)',
      'Email support',
    ],
    notIncluded: [
      'Multi-user (1 user only)',
      'Schedule Butler (rotation management)',
      'Note Butler',
      'Water Report Butler',
    ],
    perInspectionUpsell:
      'Need legal-grade AI review? Add Opus 4.7 deep review on any inspection for $2.',
  },
  {
    code: 'crew',
    name: 'Crew',
    monthlyPrice: 149,
    annualPrice: 1490,
    foundingPrice: 99,
    foundingSpots: 50,
    badge: 'Most Popular',
    tagline: 'For small companies running real operations.',
    inspections: 'Unlimited',
    users: '5 users included',
    ai: 'Sonnet 4.6 review on every submit',
    features: [
      'Everything in Solo, plus:',
      'Up to 5 users (additional users $25/mo)',
      'Schedule Butler — on-call rotation + dispatch',
      'Role-based permissions (admin, supervisor, inspector, staff)',
      'Audit trail + compliance export',
      'QuickBooks export',
      'Priority email + chat support',
    ],
    notIncluded: [
      'Note Butler',
      'Water Report Butler',
      'API / webhooks',
      'White-label client portal',
    ],
    perInspectionUpsell:
      'Need legal-grade AI review? Add Opus 4.7 deep review on any inspection for $2.',
  },
  {
    code: 'pro',
    name: 'Pro',
    monthlyPrice: 299,
    annualPrice: 2990,
    foundingPrice: 199,
    foundingSpots: 20,
    badge: null,
    tagline: 'For established multi-crew shops who outgrew Spectora.',
    inspections: 'Unlimited',
    users: 'Unlimited users',
    ai: 'Opus 4.7 deep review on every submit',
    features: [
      'Everything in Crew, plus:',
      'Unlimited users',
      'Note Butler (included)',
      'Water Report Butler (included)',
      'Opus 4.7 AI review (the most capable — used for legal-grade narratives)',
      'API + webhooks',
      'White-label client portal (your logo)',
      'Custom report branding + custom domain',
      'Phone support + dedicated onboarding',
    ],
    notIncluded: [],
    perInspectionUpsell: null,
  },
];

const FAQS = [
  {
    q: 'Does the founding-member price really lock for life?',
    a: 'Yes, as long as your subscription stays active. When we raise public pricing, you keep your rate.',
  },
  {
    q: 'What happens if I exceed 100 inspections in a month?',
    a: 'Nothing. Unlimited means unlimited. We will never charge you for being busy.',
  },
  {
    q: 'Can I switch tiers later?',
    a: 'Yes, anytime. Upgrades take effect immediately; downgrades take effect at next renewal.',
  },
  {
    q: "What's the difference between Sonnet 4.6 and Opus 4.7 review?",
    a: 'Sonnet 4.6 (Solo/Crew default) catches ~90% of issues — typos, severity inconsistencies, missing photos. Opus 4.7 (Pro default) adds deeper reasoning across long reports — better for legal disputes or complex narratives. Solo/Crew can add Opus on individual reports for $2.',
  },
  {
    q: "What if I don't want AI review at all?",
    a: 'You can disable it. Solo tier without AI review costs the same — the AI is included, not required.',
  },
  {
    q: 'Can I add more users to my plan?',
    a: 'Solo is single-user. Crew includes 5, add more at $25/mo each. Pro is unlimited.',
  },
  {
    q: 'Do I need a credit card to sign up?',
    a: "Right now, no. We're in alpha and not processing payments yet. Sign up reserves your founding-member spot. When billing launches, we'll email you to set up payment — and your price is locked in.",
  },
  {
    q: 'What if I want to cancel?',
    a: 'Cancel anytime from your account dashboard. No questions, no retention call. You keep access through the end of your billing period.',
  },
];

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PricingPage() {
  const [billing, setBilling] = useState('annual'); // 'annual' | 'monthly'

  return (
    <div style={{ backgroundColor: '#FFFFFF', color: '#111827', minHeight: '100vh' }}>
      <PublicTopNav />

      {/* Hero */}
      <header style={{ textAlign: 'center', padding: '3rem 1.5rem 1.5rem', maxWidth: 820, margin: '0 auto' }}>
        <h1 style={{ fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.025em', marginBottom: '0.75rem' }}>
          Simple pricing. Every inspection covered. No surprise charges.
        </h1>
        <p style={{ color: '#4B5563', fontSize: '1.0625rem', lineHeight: 1.55, margin: 0 }}>
          AI review included on every submit. Unlimited inspections. Founding-member pricing locked for life.
        </p>
      </header>

      {/* Annual / monthly toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
        <div
          role="tablist"
          aria-label="Billing period"
          style={{
            display: 'inline-flex',
            backgroundColor: '#F3F4F6',
            borderRadius: '999px',
            padding: '4px',
            gap: '4px',
          }}
        >
          {['annual', 'monthly'].map((opt) => (
            <button
              key={opt}
              type="button"
              role="tab"
              aria-selected={billing === opt}
              onClick={() => setBilling(opt)}
              style={{
                padding: '0.5rem 1.25rem',
                borderRadius: '999px',
                border: 'none',
                backgroundColor: billing === opt ? '#FFFFFF' : 'transparent',
                color: billing === opt ? '#111827' : '#6B7280',
                fontWeight: 600,
                fontSize: '0.9375rem',
                cursor: 'pointer',
                boxShadow: billing === opt ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
                textTransform: 'capitalize',
              }}
            >
              {opt}
              {opt === 'annual' && (
                <span style={{ marginLeft: '0.375rem', color: '#16A34A', fontSize: '0.75rem' }}>
                  save 17%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tier cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}
      >
        {TIERS.map((tier) => {
          const isCrew = tier.code === 'crew';
          const displayPrice = billing === 'annual'
            ? Math.round(tier.annualPrice / 12)
            : tier.monthlyPrice;
          const billedNote = billing === 'annual'
            ? `$${tier.annualPrice}/year, billed annually`
            : `$${tier.monthlyPrice}/mo, billed monthly`;

          return (
            <Card
              key={tier.code}
              variant={isCrew ? 'elevated' : 'outline'}
              padding="lg"
              style={{
                ...(isCrew
                  ? {
                      borderColor: '#2563EB',
                      borderWidth: '2px',
                      boxShadow: '0 10px 30px rgba(37, 99, 235, 0.15)',
                      transform: 'translateY(-4px)',
                    }
                  : {}),
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Badge */}
              <div style={{ minHeight: '1.75rem', marginBottom: '0.5rem' }}>
                {tier.badge && <Badge color="blue" variant="solid">{tier.badge}</Badge>}
              </div>

              {/* Name + tagline */}
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{tier.name}</h2>
              <p style={{ color: '#6B7280', fontSize: '0.9375rem', margin: '0.375rem 0 1.25rem' }}>
                {tier.tagline}
              </p>

              {/* Price */}
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '2.75rem', fontWeight: 700, letterSpacing: '-0.025em' }}>
                  ${displayPrice}
                </span>
                <span style={{ color: '#6B7280', fontSize: '1rem', marginLeft: '0.375rem' }}>/mo</span>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: '0.8125rem', margin: '0 0 1rem' }}>
                {billedNote}
              </p>

              {/* Founding-member offer */}
              <div
                style={{
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '8px',
                  padding: '0.625rem 0.75rem',
                  marginBottom: '1.25rem',
                  fontSize: '0.8125rem',
                  color: '#B45309',
                  lineHeight: 1.45,
                }}
              >
                <strong>Founding member:</strong> First {tier.foundingSpots} customers lock in $
                {tier.foundingPrice}/mo for life.
              </div>

              {/* Stats row */}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#374151' }}>
                <li style={{ marginBottom: '0.375rem' }}><strong>{tier.users}</strong></li>
                <li style={{ marginBottom: '0.375rem' }}><strong>{tier.inspections}</strong> inspections</li>
                <li><strong>AI:</strong> {tier.ai}</li>
              </ul>

              {/* Feature list */}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.25rem', flexGrow: 1 }}>
                {tier.features.map((feat) => (
                  <li
                    key={feat}
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#374151',
                    }}
                  >
                    <CheckIcon />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>

              {/* Not included */}
              {tier.notIncluded.length > 0 && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem' }}>
                    Not included
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {tier.notIncluded.map((ni) => (
                      <li
                        key={ni}
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          alignItems: 'flex-start',
                          marginBottom: '0.375rem',
                          fontSize: '0.8125rem',
                          color: '#9CA3AF',
                        }}
                      >
                        <CrossIcon />
                        <span>{ni}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA */}
              <Button
                as={Link}
                to={`/register?plan=${tier.code}`}
                variant={isCrew ? 'primary' : 'outline'}
                size="lg"
                fullWidth
              >
                Join as {tier.name}
              </Button>

              {/* Per-inspection upsell */}
              {tier.perInspectionUpsell && (
                <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.75rem', textAlign: 'center', lineHeight: 1.5 }}>
                  {tier.perInspectionUpsell}
                </p>
              )}
            </Card>
          );
        })}
      </div>

      {/* FAQ */}
      <section style={{ maxWidth: 820, margin: '4rem auto 0', padding: '0 1.5rem' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, textAlign: 'center', marginBottom: '2rem' }}>
          Frequently asked questions
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {FAQS.map((item) => (
            <details
              key={item.q}
              style={{
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '1rem 1.25rem',
              }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '1rem', color: '#111827', listStyle: 'none' }}>
                {item.q}
              </summary>
              <p style={{ marginTop: '0.75rem', color: '#4B5563', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

export default PricingPage;
